import {useCallback, useMemo} from 'react';
import {useDocumentContext} from '@/components/document/context';
import {useDocumentStore} from '@/components/document/store';
import {ValidateSectionMode} from '@/types/document/store';

export const useGroupModalValidation = (groupId: string) => {
  const {valueResolver, store, visibilitySet, documentModel, currentSectionId: currentSection} = useDocumentContext();
  const validity = useDocumentStore(state => state.validity);

  const getFields = useMemo(
    () => () => {
      const isArrayGroupRow = groupId.includes('#');

      if (isArrayGroupRow) {
        return valueResolver({$ref: `_${groupId}[fields]`})
          .split(',')
          .filter(field => visibilitySet.has(field));
      } else {
        const group = documentModel.sections.flatMap(section => section.groups).find(group => group.id === groupId);
        if (!group || !('fields' in group)) return [];
        return group.fields.map(field => field.id).filter(field => visibilitySet.has(field));
      }
    },
    [groupId, visibilitySet, valueResolver, documentModel],
  );

  const validateAllFields = useCallback(() => {
    const fields = getFields();

    const dependentSections = documentModel.sections.find(section => section.id === currentSection)?.displayWith ?? [];

    // Field can either be directly in currentSection or in a dependent one.
    // Validate all visible fields so that the form inputs update themselves.
    for (const section of [currentSection, ...dependentSections]) {
      store.getState().validateSection(section, {
        documentModel,
        valueResolver,
        validateMode: ValidateSectionMode.Fields,
        validateFields: fields,
      });
    }
  }, [store, valueResolver, getFields, currentSection, documentModel]);

  const isGroupValid = useMemo(() => {
    const fields = getFields();

    const invalidFields: Set<string> = new Set();
    const dependentSections = documentModel.sections.find(section => section.id === currentSection)?.displayWith ?? [];

    for (const field of fields) {
      const fieldValidityPresent = validity[currentSection]?.fields[field] != undefined;

      // Field can either be directly in currentSection or in a dependent one.
      if (fieldValidityPresent) {
        const fieldValidity = validity[currentSection]?.fields[field];

        if (
          fieldValidity.valid === false ||
          ('willBeValidOnceTouched' in fieldValidity && fieldValidity.willBeValidOnceTouched === false)
        ) {
          invalidFields.add(field);
        }
      } else {
        for (const dependentSection of dependentSections) {
          const fieldValidity = validity[dependentSection]?.fields[field];

          if (
            fieldValidity?.valid === false ||
            (fieldValidity &&
              'willBeValidOnceTouched' in fieldValidity &&
              fieldValidity.willBeValidOnceTouched === false)
          ) {
            invalidFields.add(field);
          }
        }
      }
    }
    return invalidFields.size === 0;
  }, [validity, documentModel, currentSection, getFields]);

  return {isGroupValid, validateAllFields};
};
